import type { LIABILITY_UUID_INFO } from './liabilityVerification'
import type {
  INCOME_FREQUENCY,
  INCOME_TYPE,
  LOAN_APPLICATION_STATUS,
  LOAN_CLASS,
  LOAN_TYPE,
  PARTNER_IDS,
  REF_EMPLOYMENT_STATUS,
  REF_LIVING_SITUATION,
  REF_RESIDENCY_STATUS,
  REJECTION_CODES,
  SUPPORTED_REGION,
} from '.'

export interface LOAN_APPLICATION {
  loanId: number
  loanUuid: string
  loanClass: LOAN_CLASS
  loanType: LOAN_TYPE
  source: string
  partnerId: PARTNER_IDS
  status: LOAN_APPLICATION_STATUS
  rejectionCode: REJECTION_CODES
  step: any
  agentId: number
  settledAgentId: number
  helpFindNewCar: boolean
  personalLoan: boolean
  emailSentAt: string
  smsSentAt: string
  createdAt: string
  updatedAt: string
  data: object
  region: SUPPORTED_REGION
}

export interface ADDRESS {
  addressFull: string
  addressUnit: string
  addressBuildingName?: string
  addressStreetNumber: string
  addressStreet: string
  addressSuburb: string
  addressTownCity: string
  addressState: string
  addressPostCode: string
  useManualAddress?: boolean
  addressDurationYears: string
  addressDurationMonths: string
}

export interface PREVIOUS_ADDRESS {
  previousAddressFull: string
  previousAddressUnit: string
  previousAddressBuildingName?: string
  previousAddressStreetNumber: string
  previousAddressStreet: string
  previousAddressSuburb: string
  previousAddressTownCity: string
  previousAddressState: string
  previousAddressPostCode: string
  useManualPreviousAddress?: boolean
  previousAddressDurationYears: string
  previousAddressDurationMonths: string
}

export interface SECOND_PREVIOUS_ADDRESS {
  secondPreviousAddressFull: string
  secondPreviousAddressUnit: string
  secondPreviousAddressBuildingName?: string
  secondPreviousAddressStreetNumber: string
  secondPreviousAddressStreet: string
  secondPreviousAddressSuburb: string
  secondPreviousAddressTownCity: string
  secondPreviousAddressState: string
  secondPreviousAddressPostCode: string
  useManualSecondPreviousAddress?: boolean
  secondPreviousAddressDurationYears: string
  secondPreviousAddressDurationMonths: string
}

export interface CUSTOMER {
  userId: number
  uuid: string
  email?: string
  mobile?: string
  firstName?: string
  middleName?: string
  lastName?: string
  dateOfBirth?: string
  employmentStatus?: REF_EMPLOYMENT_STATUS
  livingSituation?: REF_LIVING_SITUATION
  residencyStatus?: REF_RESIDENCY_STATUS
  address?: ADDRESS
  previousAddress?: PREVIOUS_ADDRESS
  secondPreviousAddress?: SECOND_PREVIOUS_ADDRESS
  state?: string
  postCode?: number
  lastLogin?: string
  licence?: string
  createdAt?: string
  updatedAt?: string
  region: SUPPORTED_REGION
}

export interface OTHER_INCOME {
  type: INCOME_TYPE
  amount: string
  frequency: INCOME_FREQUENCY
  salesforceId?: string
  employmentType?: string
}

export interface LOAN_APPLICATION_LIABILITY {
  lenderName: string
  liabilityUuid?: string
  toConsolidateLiability: boolean | undefined | string
}

export interface LOAN_APPLICATION_DATA_VEHICLE_LOAN extends LOAN_APPLICATION_LIABILITY {
  amount: number
  balance: number
  monthly: number
  jointLoanField: boolean | undefined | string // old joint field name, existing for old loan applications before agent-4366
  joint: string // new joint field name
  lookingToTradeIn?: boolean | string
  termRemaining?: number
  hasBalanceVarianceReason?: boolean
  hasRepaymentVarianceReason?: boolean
}

export interface LOAN_APPLICATION_DATA_CREDIT_CARD extends LOAN_APPLICATION_LIABILITY {
  limit: number
  balance: number
  jointLoanField: boolean | undefined | string
  joint: string
  interestRate?: number
  hasLimitVarianceReason?: boolean
}

export interface LOAN_APPLICATION_DATA_MORTGAGE extends LOAN_APPLICATION_LIABILITY {
  amount: number
  balance: number
  monthly: number
  jointLoanField: boolean | undefined | string
  joint: string
  termRemaining?: number
  hasBalanceVarianceReason?: boolean
  hasRepaymentVarianceReason?: boolean
}

export interface LOAN_APPLICATION_DATA_PAY_LATER extends LOAN_APPLICATION_LIABILITY {
  limit: number
  balance: number
  monthly: number
  jointLoanField: boolean | undefined | string
  joint: string
  termRemaining?: number
}

export interface LOAN_APPLICATION_DATA_PERSONAL_LOAN extends LOAN_APPLICATION_LIABILITY {
  amount: number
  balance: number
  monthly: number
  jointLoanField: boolean | undefined | string
  joint: string
  termRemaining?: number
  hasBalanceVarianceReason?: boolean
  hasRepaymentVarianceReason?: boolean
}

export interface LOAN_APPLICATION_DATA_OTHER_LIABILITY extends LOAN_APPLICATION_LIABILITY {
  amount: number
  joint: string
  termRemaining?: number
}

export interface LOAN_APPLICATION_DATA_EARLY_WAGE extends LOAN_APPLICATION_LIABILITY {
  amount: number
  balance: number
  monthly: number
  jointLoanField: boolean | undefined | string
  joint: string
}

export enum LIABILITY_VERIFIED_FIELDS {
  BALANCE = 'balance',
  LIMIT = 'limit',
  REPAYMENT = 'repayment',
}

export interface LOAN_APPLICATION_DATA_LIABILITY_META {
  verified?: boolean
  verifiedFields?: LIABILITY_VERIFIED_FIELDS[]
  liabilityUuids: LIABILITY_UUID_INFO[]
}
